import AddBoxIcon from "@mui/icons-material/AddBox";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { Box, Button, IconButton } from "@mui/material";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import type { ReactNode } from "react";

// Row menu column of the Forms grids: the list icon used across the project, opening the
// row's context menu
export const listMenuColumn: ColDef = {
	colId: "actions",
	headerName: "",
	minWidth: 65,
	maxWidth: 65,
	sortable: false,
	cellRenderer: (params: ICellRendererParams) => (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
		>
			<Button
				fullWidth
				onClick={(e) =>
					// biome-ignore lint/suspicious/noExplicitAny: mouseEvent is missing from the typings
					(params.api as any).showContextMenu({
						column: params.column,
						rowNode: params.node,
						mouseEvent: e.nativeEvent,
					})
				}
			>
				<FormatListBulletedSharpIcon
					style={{ height: "auto" }}
					color="action"
				/>
			</Button>
		</Box>
	),
};

// Border shared by every box inside a Forms dialog
export const modalBoxSx = {
	border: 1,
	borderColor: "divider",
	borderRadius: 1,
} as const;

type Props = {
	// Fixed height; without it the box fills the rest of its column
	height?: string;
	onAdd?: () => void;
	addDisabled?: boolean;
	children: ReactNode;
};

// A grid inside a Forms dialog: a bordered box of fixed height, so the dialog keeps its size
// whatever the list holds, with the "+" add icon at the bottom left as on the Forms page
export const ModalGridBox = ({
	height,
	onAdd,
	addDisabled = false,
	children,
}: Props) => (
	<Box
		sx={{
			position: "relative",
			...(height ? { height } : { flex: 1, minHeight: 0 }),
			...modalBoxSx,
			overflow: "hidden",
			display: "flex",
			flexDirection: "column",
			".ag-root-wrapper": { border: "none", borderRadius: 0 },
		}}
	>
		<Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
		{onAdd && (
			<Box sx={{ height: 52, flexShrink: 0, position: "relative" }}>
				<IconButton
					onClick={onAdd}
					disabled={addDisabled}
					aria-label="add"
					color="primary"
					sx={{ position: "absolute", left: 8, bottom: 8, p: 0 }}
				>
					<AddBoxIcon style={{ width: 35, height: 35 }} />
				</IconButton>
			</Box>
		)}
	</Box>
);
